import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import usePortalPopover from './usePortalPopover';
import { ConfirmPopover } from '../styles/TableStyles';
import { SPEC_STATUSES, statusColor, normalizeStatus } from '../utils/specStatus';

// Lifecycle status control: a colored chip that opens a portaled dropdown of the
// available statuses. With `confirm`, picking a different status opens an in-app
// ConfirmPopover before committing (the Manage table inline-flip path); without it,
// the choice applies immediately (the edit drawer + Entry Form).
//
// Defaults to the Customer Spec status set (utils/specStatus.js); pass `statuses` +
// `colorFor` + `normalize` to drive it from another set (e.g. utils/pidStatus.js for
// Package Index). Colors are token-based + WCAG-AA. The outer span stops click
// propagation so the chip never triggers a parent row click.

const SIZES = {
  sm: { padding: '3px 10px', font: '0.78rem' },
  md: { padding: '5px 12px', font: '0.9rem' },
};

export default function StatusPicker({
  status,
  onChange,
  confirm = false,
  disabled = false,
  size = 'md',
  ariaLabel = 'Status',
  statuses = SPEC_STATUSES,
  colorFor = statusColor,
  normalize = normalizeStatus,
}) {
  const menu = usePortalPopover({ desiredMax: 240 });
  const [pending, setPending] = useState(null); // status awaiting confirmation
  const cur = normalize(status);
  const { bg, fg } = colorFor(cur);
  const sz = SIZES[size] || SIZES.md;

  const choose = (next) => {
    menu.setOpen(false);
    if (next === cur) return;
    if (confirm) setPending(next);
    else if (onChange) onChange(next);
  };

  return (
    <span onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex' }}>
      <button
        ref={menu.anchorRef}
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={menu.open}
        disabled={disabled || !!pending}
        onClick={() => menu.setOpen((o) => !o)}
        style={{
          background: bg,
          color: fg,
          border: 'none',
          borderRadius: 999,
          padding: sz.padding,
          fontSize: sz.font,
          fontWeight: 700,
          cursor: disabled ? 'default' : 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          lineHeight: 1.2,
          whiteSpace: 'nowrap',
        }}
      >
        {cur}
        <span aria-hidden style={{ fontSize: '0.7em', opacity: 0.85 }}>▾</span>
      </button>

      {menu.open && createPortal(
        <div
          ref={menu.menuRef}
          role="listbox"
          aria-label={ariaLabel}
          style={{
            ...menu.menuStyle,
            width: 'auto',
            minWidth: 190,
            background: 'var(--color-surface)',
            border: '1px solid var(--shadow-a08)',
            borderRadius: 10,
            boxShadow: '0 8px 24px var(--shadow-a15)',
            padding: 6,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {statuses.map((opt) => {
            const c = colorFor(opt);
            const selected = opt === cur;
            return (
              <button
                key={opt}
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => choose(opt)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  width: '100%',
                  border: 'none',
                  background: selected ? 'var(--shadow-a05)' : 'transparent',
                  cursor: 'pointer',
                  padding: '6px 8px',
                  borderRadius: 8,
                  textAlign: 'left',
                }}
              >
                <span style={{ width: 14, height: 14, borderRadius: 4, background: c.bg, flex: '0 0 auto', border: '1px solid var(--shadow-a08)' }} />
                <span style={{ fontWeight: selected ? 800 : 500 }}>{opt}</span>
              </button>
            );
          })}
        </div>,
        document.body
      )}

      {confirm && (
        <ConfirmPopover
          open={!!pending}
          anchorRef={menu.anchorRef}
          message={pending ? `Set status to “${pending}”?` : 'Confirm?'}
          confirmLabel="Set status"
          onConfirm={() => { const p = pending; setPending(null); if (p && onChange) onChange(p); }}
          onCancel={() => setPending(null)}
        />
      )}
    </span>
  );
}
